import React, { useState, useEffect } from 'react';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Image from '../../../components/appImage';
import { motion, AnimatePresence } from 'framer-motion';

const ItemDetailsModal = ({ item, isOpen, onClose, onSave, userRole = 'admin' }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(item || {});
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (isOpen && item) {
      setFormData(item);
      setEditMode(false);
    }
  }, [isOpen, item]);

  const handleClose = () => {
    onClose();
  };

  if (!isOpen || !item) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    })?.format(amount || 0);
  };

  const formatDate = (dateString, includeTime = true) => {
    if (!dateString) return '-';
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    };
    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }
    return new Intl.DateTimeFormat('es-MX', options)?.format(new Date(dateString));
  };

  const getStockStatus = (current, minimum) => {
    if (current === 0) return { status: 'out-of-stock', color: 'text-red-400', label: 'Sin stock' };
    if (current <= minimum) return { status: 'low-stock', color: 'text-amber-400', label: 'Stock bajo' };
    if (current <= minimum * 2) return { status: 'adequate', color: 'text-cyan-300', label: 'Adecuado' };
    return { status: 'overstock', color: 'text-cyan-300', label: 'Sobre stock' };
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: 'no-expiry', color: 'text-slate-400', label: 'Sin vencimiento' };

    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { status: 'expired', color: 'text-red-400', label: 'Vencido' };
    if (diffDays <= 7) return { status: 'critical', color: 'text-red-400', label: 'Crítico' };
    if (diffDays <= 30) return { status: 'warning', color: 'text-amber-400', label: 'Próximo' };
    return { status: 'safe', color: 'text-cyan-300', label: 'Vigente' };
  };

  const calculateMargin = (cost, price) => {
    if (!cost || !price || cost <= 0) return 0;
    return ((price - cost) / cost) * 100;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(formData);
    setEditMode(false);
  };

  const handleCancel = () => {
    setFormData(item);
    setEditMode(false);
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'adjust_stock':
        // TODO
        break;
      case 'print_label':
        // TODO
        break;
      case 'duplicate':
        // TODO
        break;
      default:
        break;
    }
  };

  const locationOptions = [
    { value: 'warehouse-a', label: 'Almacén A - Principal' },
    { value: 'warehouse-b', label: 'Almacén B - Secundario' },
    { value: 'workshop-1', label: 'Taller 1 - Diagnóstico' },
    { value: 'workshop-2', label: 'Taller 2 - Reparación' },
    { value: 'office', label: 'Oficina - Herramientas' },
    { value: 'branch-1', label: 'Sucursal Norte' },
    { value: 'branch-2', label: 'Sucursal Sur' }
  ];

  const supplierOptions = [
    { value: 'TSV', label: 'TRANSERVICIOS' },
    { value: 'J.A.M', label: 'Juan Antonio Martinez' },
    { value: 'J.L', label: 'Juan Loza' },
    { value: 'J.T', label: 'Julio Tacuacha' },
    { value: 'O.G', label: 'Oscar Gonzalez' }
  ];

  const unitOptions = [
    { value: 'piece', label: 'Pieza' },
    { value: 'box', label: 'Caja' },
    { value: 'liter', label: 'Litro' },
    { value: 'kg', label: 'Kilogramo' },
    { value: 'meter', label: 'Metro' },
    { value: 'unit', label: 'Unidad' },
    { value: 'pair', label: 'Par' },
    { value: 'set', label: 'Juego' }
  ];

  const categoryOptions = [
    { value: 'electronic-components', label: 'Componentes Electrónicos' },
    { value: 'sensors', label: 'Sensores' },
    { value: 'cables', label: 'Cables y Conectores' },
    { value: 'control-modules', label: 'Módulos de Control' },
    { value: 'diagnostic-tools', label: 'Herramientas de Diagnóstico' },
    { value: 'lighting', label: 'Iluminación' },
    { value: 'power-systems', label: 'Sistemas de Energía' },
    { value: 'perishable', label: 'Productos Perecederos' },
    { value: 'medications', label: 'Medicamentos' },
    { value: 'tools', label: 'Herramientas' },
    { value: 'consumables', label: 'Consumibles' }
  ];

  const tabs = [
    { id: 'details', label: 'Detalles', icon: 'Package' },
    { id: 'history', label: 'Historial', icon: 'History' },
    { id: 'movements', label: 'Movimientos', icon: 'Activity' },
    { id: 'analytics', label: 'Análisis', icon: 'BarChart3' },
    { id: 'photos', label: 'Multimedia', icon: 'Image' }
  ];

  const movementHistory = item?.movementHistory || [];
  const recentMovements = item?.recentMovements || [];
  const images = item?.images || [];

  const stockStatus = getStockStatus(item?.currentStock || 0, item?.minimumStock || 0);
  const expiryStatus = getExpiryStatus(item?.expiryDate);
  const margin = calculateMargin(item?.cost, item?.price);
  const stockValue = (item?.currentStock || 0) * (item?.cost || 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={handleClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ type: 'spring', damping: 24, stiffness: 260 }}
              className="
                relative w-full max-w-6xl max-h-[95vh]
                bg-slate-900
                rounded-2xl
                shadow-[0_0_0_1px_rgba(15,23,42,0.9),0_30px_80px_rgba(0,0,0,0.9)]
                border border-cyan-800/70
                overflow-hidden
              "
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-cyan-800/60 bg-slate-900">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {item?.image ? (
                      <Image
                        src={item?.image}
                        alt={item?.name}
                        className="w-14 h-14 rounded-xl object-cover border border-cyan-800/70 bg-slate-900"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-slate-800 border border-cyan-800/70 flex items-center justify-center">
                        <Icon name="Package" size={24} className="text-cyan-300/70" />
                      </div>
                    )}
                    <div
                      className={`
                        absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-slate-900
                        ${stockStatus.status === 'out-of-stock'
                          ? 'bg-red-400'
                          : stockStatus.status === 'low-stock'
                            ? 'bg-amber-400'
                            : 'bg-cyan-400'
                        }
                      `}
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-slate-50">
                      {item?.name}
                    </h2>
                    <div className="flex items-center flex-wrap gap-3 mt-1">
                      <p className="text-xs sm:text-sm text-cyan-100 font-mono bg-slate-800 px-2 py-1 rounded border border-cyan-800/70">
                        {item?.code}
                      </p>
                      {item?.barcode && (
                        <p className="text-xs sm:text-sm text-slate-300 flex items-center space-x-1">
                          <Icon name="Barcode" size={14} className="text-cyan-400/80" />
                          <span>{item?.barcode}</span>
                        </p>
                      )}
                      <div
                        className={`
                          px-2 py-1 rounded-full text-[11px] font-medium 
                          border
                          ${item?.status === 'active'
                            ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40'
                            : item?.status === 'inactive'
                              ? 'bg-slate-600/20 text-slate-300 border-slate-500/50'
                              : 'bg-red-500/10 text-red-300 border-red-500/40'
                          }
                        `}
                      >
                        {item?.status === 'active'
                          ? 'Activo'
                          : item?.status === 'inactive'
                            ? 'Inactivo'
                            : 'Descontinuado'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {!editMode && userRole === 'admin' && (
                    <div className="hidden sm:flex items-center space-x-2 mr-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAction('adjust_stock')}
                        iconName="Plus"
                        iconSize={14}
                        className="border-cyan-700/70 text-cyan-100 hover:bg-cyan-500/10"
                      >
                        Ajustar stock
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickAction('print_label')}
                        iconName="Printer"
                        iconSize={14}
                        className="border-slate-600 text-slate-200 hover:bg-slate-800/80"
                      >
                        Etiqueta
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setEditMode(true)}
                        iconName="Edit"
                        iconSize={16}
                        className="border-cyan-600/80 text-cyan-100 hover:bg-cyan-500/10"
                      >
                        Editar
                      </Button>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClose}
                    iconName="X"
                    iconSize={20}
                    className="text-slate-400 hover:text-slate-100 hover:bg-slate-800/90 rounded-xl"
                  />
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-cyan-800/50 bg-slate-900/95">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center space-x-2 px-5 sm:px-6 py-3 text-xs sm:text-sm font-medium 
                      transition-all duration-200 border-b-2
                      ${activeTab === tab.id
                        ? 'text-cyan-300 border-cyan-400 bg-cyan-500/5'
                        : 'text-slate-400 border-transparent hover:text-slate-200 hover:bg-slate-800/70'
                      }
                    `}
                  >
                    <Icon
                      name={tab.icon}
                      size={16}
                      className={activeTab === tab.id ? 'text-cyan-300' : 'text-slate-400'}
                    />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto max-h-[calc(95vh-190px)] bg-slate-900">
                <div className="p-6 space-y-6">
                  {activeTab === 'details' && (
                    <div className="space-y-6">
                      {/* Stats Cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-slate-800/90 rounded-xl p-4 border border-cyan-800/70">
                          <div className="text-xs text-slate-400 mb-1">Stock actual</div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-2 h-2 rounded-full ${stockStatus.color}`} />
                            <div className="text-2xl font-semibold text-slate-50">
                              {item?.currentStock || 0}
                            </div>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">
                            Mínimo: {item?.minimumStock || 0}
                          </div>
                        </div>

                        <div className="bg-slate-800/90 rounded-xl p-4 border border-cyan-800/70">
                          <div className="text-xs text-slate-400 mb-1">Valor total</div>
                          <div className="text-2xl font-semibold text-slate-50">
                            {formatCurrency(stockValue)}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">
                            Costo unitario: {formatCurrency(item?.cost || 0)}
                          </div>
                        </div>

                        <div className="bg-slate-800/90 rounded-xl p-4 border border-cyan-800/70">
                          <div className="text-xs text-slate-400 mb-1">Margen</div>
                          <div className="text-2xl font-semibold text-cyan-300">
                            {margin.toFixed(1)}%
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">
                            {formatCurrency((item?.price || 0) - (item?.cost || 0))} por unidad
                          </div>
                        </div>

                        <div className="bg-slate-800/90 rounded-xl p-4 border border-cyan-800/70">
                          <div className="text-xs text-slate-400 mb-1">Vencimiento</div>
                          <div className="text-lg font-semibold text-slate-50">
                            {item?.expiryDate ? formatDate(item.expiryDate, false) : 'N/A'}
                          </div>
                          <div className={`text-[11px] font-medium mt-1 ${expiryStatus.color}`}>
                            {expiryStatus.label}
                          </div>
                        </div>
                      </div>

                      {/* Form Fields */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <Input
                            label="Nombre del producto"
                            value={editMode ? formData?.name : item?.name}
                            onChange={(e) => handleInputChange('name', e?.target?.value)}
                            disabled={!editMode}
                            className="bg-slate-900 border-slate-700 text-slate-50"
                          />

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                              label="Código/SKU"
                              value={editMode ? formData?.code : item?.code}
                              onChange={(e) => handleInputChange('code', e?.target?.value)}
                              disabled={!editMode}
                              className="bg-slate-900 border-slate-700 text-slate-50"
                            />

                            <Input
                              label="Código de barras"
                              value={editMode ? formData?.barcode : item?.barcode}
                              onChange={(e) => handleInputChange('barcode', e?.target?.value)}
                              disabled={!editMode}
                              className="bg-slate-900 border-slate-700 text-slate-50"
                            />
                          </div>

                          <Select
                            label="Categoría"
                            options={categoryOptions}
                            value={editMode ? formData?.category : item?.category}
                            onChange={(value) => handleInputChange('category', value)}
                            disabled={!editMode}
                          />

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                              label="Stock actual"
                              type="number"
                              value={editMode ? formData?.currentStock : item?.currentStock}
                              onChange={(e) =>
                                handleInputChange(
                                  'currentStock',
                                  parseInt(e?.target?.value || '0', 10) || 0
                                )
                              }
                              disabled={!editMode}
                              className="bg-slate-900 border-slate-700 text-slate-50"
                            />

                            <Input
                              label="Stock mínimo"
                              type="number"
                              value={editMode ? formData?.minimumStock : item?.minimumStock}
                              onChange={(e) =>
                                handleInputChange(
                                  'minimumStock',
                                  parseInt(e?.target?.value || '0', 10) || 0
                                )
                              }
                              disabled={!editMode}
                              className="bg-slate-900 border-slate-700 text-slate-50"
                            />
                          </div>

                          <Select
                            label="Ubicación"
                            options={locationOptions}
                            value={editMode ? formData?.location : item?.location}
                            onChange={(value) => handleInputChange('location', value)}
                            disabled={!editMode}
                          />
                        </div>

                        <div className="space-y-4">
                          <Select
                            label="Proveedor"
                            options={supplierOptions}
                            value={editMode ? formData?.supplier : item?.supplier}
                            onChange={(value) => handleInputChange('supplier', value)}
                            disabled={!editMode}
                          />

                          <Select
                            label="Unidad de medida"
                            options={unitOptions}
                            value={editMode ? formData?.unit : item?.unit}
                            onChange={(value) => handleInputChange('unit', value)}
                            disabled={!editMode}
                          />

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                              label="Precio de costo"
                              type="number"
                              step="0.01"
                              value={editMode ? formData?.cost : item?.cost}
                              onChange={(e) =>
                                handleInputChange(
                                  'cost',
                                  parseFloat(e?.target?.value || '0') || 0
                                )
                              }
                              disabled={!editMode}
                              prefix="MXN"
                              className="bg-slate-900 border-slate-700 text-slate-50"
                            />

                            <Input
                              label="Precio de venta"
                              type="number"
                              step="0.01"
                              value={editMode ? formData?.price : item?.price}
                              onChange={(e) =>
                                handleInputChange(
                                  'price',
                                  parseFloat(e?.target?.value || '0') || 0
                                )
                              }
                              disabled={!editMode}
                              prefix="MXN"
                              className="bg-slate-900 border-slate-700 text-slate-50"
                            />
                          </div>

                          <Input
                            label="Fecha de vencimiento"
                            type="date"
                            value={editMode ? formData?.expiryDate : item?.expiryDate}
                            onChange={(e) => handleInputChange('expiryDate', e?.target?.value)}
                            disabled={!editMode}
                            className="bg-slate-900 border-slate-700 text-slate-50"
                          />

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                              label="Lote"
                              value={editMode ? formData?.batch : item?.batch}
                              onChange={(e) => handleInputChange('batch', e?.target?.value)}
                              disabled={!editMode}
                              className="bg-slate-900 border-slate-700 text-slate-50"
                            />

                            <Input
                              label="Número de serie"
                              value={editMode ? formData?.serialNumber : item?.serialNumber}
                              onChange={(e) => handleInputChange('serialNumber', e?.target?.value)}
                              disabled={!editMode}
                              className="bg-slate-900 border-slate-700 text-slate-50"
                            />
                          </div>
                        </div>
                      </div>

                      <Input
                        label="Descripción"
                        type="textarea"
                        rows={3}
                        value={editMode ? formData?.description : item?.description}
                        onChange={(e) => handleInputChange('description', e?.target?.value)}
                        disabled={!editMode}
                        placeholder="Descripción detallada del producto..."
                        className="bg-slate-900 border-slate-700 text-slate-50"
                      />

                      {editMode && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex justify-end space-x-3 pt-6 border-t border-slate-800"
                        >
                          <Button
                            variant="outline"
                            onClick={handleCancel}
                            className="border-slate-600 text-slate-200 hover:bg-slate-800"
                          >
                            Cancelar
                          </Button>
                          <Button
                            variant="default"
                            onClick={handleSave}
                            iconName="Save"
                            iconSize={16}
                            className="bg-cyan-500 hover:bg-cyan-600 text-white"
                          >
                            Guardar cambios
                          </Button>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {activeTab === 'history' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-50">
                        Historial de movimientos
                      </h3>

                      {movementHistory.length === 0 ? (
                        <div className="text-center py-10 border border-dashed border-cyan-800/70 rounded-xl bg-slate-900/80">
                          <Icon
                            name="History"
                            size={40}
                            className="mx-auto mb-3 text-cyan-400/70"
                          />
                          <p className="text-sm text-slate-200">
                            No hay movimientos registrados para este producto.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {movementHistory.map((movement) => (
                            <motion.div
                              key={movement.id}
                              initial={{ opacity: 0, x: -16 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="
                                flex items-center justify-between p-4 
                                bg-slate-900/90 rounded-xl 
                                border border-cyan-800/70 
                                hover:bg-slate-800/90 transition-colors duration-150
                              "
                            >
                              <div className="flex items-center space-x-4">
                                <div
                                  className={`
                                    w-10 h-10 rounded-full flex items-center justify-center
                                    ${movement.type === 'in'
                                      ? 'bg-cyan-500/15 text-cyan-300'
                                      : movement.type === 'out'
                                        ? 'bg-red-500/20 text-red-300'
                                        : 'bg-amber-500/20 text-amber-300'
                                    }
                                  `}
                                >
                                  <Icon
                                    name={
                                      movement.type === 'in'
                                        ? 'ArrowDownLeft'
                                        : movement.type === 'out'
                                          ? 'ArrowUpRight'
                                          : 'RefreshCw'
                                    }
                                    size={16}
                                  />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-slate-50">
                                    {movement.description}
                                  </div>
                                  <div className="text-xs text-slate-400">
                                    <span className="font-medium">{movement.user}</span> •{' '}
                                    {formatDate(movement.date)}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-4">
                                <div
                                  className={`
                                    text-sm font-semibold
                                    ${movement.type === 'in'
                                      ? 'text-cyan-300'
                                      : movement.type === 'out'
                                        ? 'text-red-300'
                                        : 'text-amber-300'
                                    }
                                  `}
                                >
                                  {movement.type === 'in'
                                    ? '+'
                                    : movement.type === 'out'
                                      ? '-'
                                      : '±'}
                                  {movement.quantity}
                                </div>
                                {movement.reference && (
                                  <div className="text-xs text-slate-300 bg-slate-800 px-2 py-1 rounded border border-slate-700">
                                    {movement.reference}
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'movements' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-50">
                        Movimientos recientes
                      </h3>

                      {recentMovements.length === 0 ? (
                        <div className="text-center py-10 border border-dashed border-cyan-800/70 rounded-xl bg-slate-900/80">
                          <Icon
                            name="Activity"
                            size={40}
                            className="mx-auto mb-3 text-cyan-400/70"
                          />
                          <p className="text-sm text-slate-200">
                            No hay movimientos recientes registrados.
                          </p>
                        </div>
                      ) : (
                        <div className="bg-slate-900/90 rounded-xl border border-cyan-800/70 overflow-hidden">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-800 bg-slate-900/95">
                                <th className="text-left p-3 text-xs font-medium text-slate-400">
                                  Fecha
                                </th>
                                <th className="text-left p-3 text-xs font-medium text-slate-400">
                                  Tipo
                                </th>
                                <th className="text-left p-3 text-xs font-medium text-slate-400">
                                  Cantidad
                                </th>
                                <th className="text-left p-3 text-xs font-medium text-slate-400">
                                  Usuario
                                </th>
                                <th className="text-left p-3 text-xs font-medium text-slate-400">
                                  Referencia
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {recentMovements.map((movement) => (
                                <tr
                                  key={movement.id}
                                  className="border-b border-slate-800/70 last:border-b-0 hover:bg-slate-800/80 transition-colors"
                                >
                                  <td className="p-3 text-slate-200">
                                    {formatDate(movement.date)}
                                  </td>
                                  <td className="p-3">
                                    <span
                                      className={`
                                        inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium
                                        ${movement.type === 'purchase'
                                          ? 'bg-cyan-500/15 text-cyan-300'
                                          : movement.type === 'sale'
                                            ? 'bg-emerald-500/15 text-emerald-300'
                                            : movement.type === 'transfer'
                                              ? 'bg-amber-500/15 text-amber-300'
                                              : 'bg-slate-600/25 text-slate-200'
                                        }
                                      `}
                                    >
                                      <Icon
                                        name={
                                          movement.type === 'purchase'
                                            ? 'ShoppingCart'
                                            : movement.type === 'sale'
                                              ? 'DollarSign'
                                              : movement.type === 'transfer'
                                                ? 'Truck'
                                                : 'Settings'
                                        }
                                        size={12}
                                        className="mr-1"
                                      />
                                      {movement.type === 'purchase'
                                        ? 'Compra'
                                        : movement.type === 'sale'
                                          ? 'Venta'
                                          : movement.type === 'transfer'
                                            ? 'Transferencia'
                                            : 'Ajuste'}
                                    </span>
                                  </td>
                                  <td className="p-3 text-slate-50 font-medium">
                                    {movement.quantity}
                                  </td>
                                  <td className="p-3 text-slate-200">
                                    {movement.user}
                                  </td>
                                  <td className="p-3 text-slate-400 font-mono">
                                    {movement.reference}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'analytics' && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-slate-50">
                        Análisis del producto
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="bg-slate-900/90 rounded-xl p-5 border border-cyan-800/70">
                          <div className="text-xs text-slate-400 mb-2">
                            Valor en inventario
                          </div>
                          <div className="text-3xl font-semibold text-slate-50 mb-1">
                            {formatCurrency(stockValue)}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {item?.currentStock || 0} unidades x{' '}
                            {formatCurrency(item?.cost || 0)}
                          </div>
                        </div>

                        <div className="bg-slate-900/90 rounded-xl p-5 border border-cyan-800/70">
                          <div className="text-xs text-slate-400 mb-2">
                            Margen de ganancia
                          </div>
                          <div className="text-3xl font-semibold text-cyan-300 mb-1">
                            {margin.toFixed(1)}%
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {formatCurrency((item?.price || 0) - (item?.cost || 0))} por unidad
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-900/90 rounded-xl p-6 border border-dashed border-cyan-800/70">
                        <div className="flex items-center gap-3 mb-2">
                          <Icon
                            name="BarChart3"
                            size={20}
                            className="text-cyan-300/80"
                          />
                          <span className="text-sm font-medium text-slate-100">
                            Gráficos y tendencias
                          </span>
                        </div>
                        <p className="text-sm text-slate-400">
                          Aquí puedes integrar más adelante gráficos de consumo, ventas,
                          rotación y otras métricas usando tus datos reales.
                        </p>
                      </div>
                    </div>
                  )}

                  {activeTab === 'photos' && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-50">
                        Multimedia del producto
                      </h3>

                      {images.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                          {images.map((image, index) => (
                            <motion.div
                              key={index}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="relative group aspect-square rounded-xl overflow-hidden border border-cyan-800/70 bg-slate-900"
                            >
                              <Image
                                src={image.url}
                                alt={image.alt || item.name}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/65 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  iconName="Maximize"
                                  iconSize={16}
                                  className="text-slate-50 hover:bg-white/15"
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  iconName="Download"
                                  iconSize={16}
                                  className="text-slate-50 hover:bg-white/15"
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  iconName="Trash2"
                                  iconSize={16}
                                  className="text-slate-50 hover:bg-red-500/25"
                                />
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 text-slate-300 border border-dashed border-cyan-800/70 rounded-xl bg-slate-900/80">
                          <Icon
                            name="Image"
                            size={48}
                            className="mx-auto mb-4 text-cyan-400/70"
                          />
                          <p className="text-sm mb-2 text-slate-100">
                            No hay imágenes cargadas
                          </p>
                          <p className="text-xs mb-4 text-slate-400">
                            Agrega imágenes para identificar mejor el producto en el sistema.
                          </p>
                          <Button
                            variant="outline"
                            iconName="Plus"
                            iconSize={16}
                            className="border-cyan-700 text-cyan-100 hover:bg-cyan-500/10"
                          >
                            Agregar imágenes
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ItemDetailsModal;
